import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { TranslateContext } from "../../App";
import { formatPrice } from "../../functions/utils";
import config from "../../configuration.json";
import Banner from "../Banner/Banner";

function ThankYou({
  configuration,
  isMobile,
  orderedItems,
  orderedTotal,
  priceFormatter,
  shippingPrice,
  userDetails,
  selectedDelivery,
  destinationType,
  invoiceNeeded,
}) {
  const language = useContext(TranslateContext).language;
  const destinationLabel =
    destinationType === "home"
      ? config.texts.destinationHome?.[language]
      : destinationType === "office"
        ? config.texts.destinationOffice?.[language]
        : destinationType === "box"
          ? config.texts.destinationBox?.[language]
          : "";
  const officeName =
    userDetails?.selectedOffice?.name ||
    userDetails?.selectedOffice?.officeName ||
    "";
  return (
    <>
      {configuration?.checkoutBanner?.show && (
        <div className="row bg-[#fcf6ef]">
          <div className="row-container">
            <Banner
              type={configuration?.checkoutBanner?.type || "warning"}
              message={configuration?.checkoutBanner?.message}
            />
          </div>
        </div>
      )}
      <div className="row">
        <div className="thank-you-section">
        <h2 className="h2-title">{config.texts.orderConfirmation[language]}</h2>
        <p>
          {config.texts.orderConfirmationText1[language]}
          <br />
          {config.texts.orderConfirmationText2[language]}
        </p>
        <p>
          {config.texts.orderConfirmationText3[language]}{" "}
          <a className="font-bold" href={`tel:${configuration?.businessInfo?.phone}`}>
            {configuration?.businessInfo?.phone}
          </a>
          <br />
          {config.texts.orderConfirmationText4[language]}{" "}
          <a className="font-bold" href={`mailto:${configuration?.businessInfo?.email}`}>
            {configuration?.businessInfo?.email}
          </a>
        </p>
        <hr />
        <h3 className="h3-title">{config.texts.orderSummary[language]}</h3>
        <div className="order-summary-container">
          <div className="order-summary">
            <div className="order-summary-row">
              <p></p> {/* Empty column for alignment */}
              <p>{config.texts.product[language]}</p>
              <p>{config.texts.quantity[language]}</p>
              <p>{config.texts.price[language]}</p>
            </div>
            <hr />
            {orderedItems.map((item, index) => (
              <React.Fragment key={index}>
                <div key={index}>
                  <div className="order-summary-row">
                    <img className="order-item-image" src={item.image.src} alt={item.image.alt} />
                    <Link to={`/${item.slug}`} className="otder-item-title">
                      {item.title[language] ? item.title[language] : item.title}
                    </Link>
                    <p className="lg:text-lg">{`${item.cartQuantity} ${config.texts.pcs[language]}.`} </p>
                    <div className="order-item-total">
                      <p>{formatPrice(item.price * item.cartQuantity, language, config)}</p>
                    </div>
                  </div>
                </div>
                <hr />
              </React.Fragment>
            ))}
            <div className="order-summary-price-breakdown-container">
              <div className="order-summary-price-breakdown">
                <p>{config.texts.shippingCost[language]}</p>
                <div>
                  {shippingPrice ? (
                    <div>
                      <p>{formatPrice(shippingPrice, language, config)}</p>
                    </div>
                  ) : (
                    <p>
                      {selectedDelivery}
                      {destinationLabel ? ` · ${destinationLabel}` : ""}
                    </p>
                  )}
                </div>
                <p>
                  {config.texts.total[language]}
                  <span>*</span>
                  <p className="w-full text-xs">{config.texts.noShippingPriceAdded[language]}</p>
                </p>
                <div className="order-summary-price-breakdown-total">
                  <p>
                    {formatPrice(
                      parseFloat(orderedTotal) + Number(shippingPrice ? shippingPrice : ""),
                      language,
                      config
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="order-summary-contact-details">
            {!isMobile && <div className="checkout-h-divider"></div>}
            <div className="order-summary-contact-details-inner">
              <h4 className="h4-title">{config.texts.shippingDetails[language]}</h4>
              <p>
                <span className="font-bold">{config.texts.shippingCarrier?.[language] || "Courier"}</span>:{" "}
                {selectedDelivery}
              </p>
              {destinationLabel && (
                <p>
                  <span className="font-bold">{config.texts.destinationType?.[language]}</span>: {destinationLabel}
                </p>
              )}
              {officeName && (
                <p>
                  <span className="font-bold">{config.texts.selectedOffice?.[language]}</span>: {officeName}
                </p>
              )}
              <p>
                <span className="font-bold">{config.texts.firstAndLastNames[language]}</span>:{" "}
                {`${userDetails?.firstName} ${userDetails?.lastName}`}
              </p>
              <p>
                <span className="font-bold">{config.texts.email[language]}</span>: {userDetails?.email}
              </p>
              <p>
                <span className="font-bold">{config.texts.phone[language]}</span>: {userDetails?.phoneNumber}
              </p>
              <p>
                <span className="font-bold">{config.texts.address[language]}</span>: {userDetails?.address}
              </p>
              <p>
                <span className="font-bold">{config.texts.city[language]}</span>: {userDetails?.city}
              </p>
              <p>
                <span className="font-bold">{config.texts.postCode[language]}</span>: {userDetails?.postalCode}
              </p>
              {invoiceNeeded && (
                <h4 className="h4-title mt-[15px] lg:mt-[20px]">{config.texts.invoiceDetails[language]}</h4>
              )}
              {userDetails?.companyName && (
                <p>
                  <span className="font-bold">{config.texts.companyName[language]}</span>: {userDetails?.companyName}
                </p>
              )}
              {userDetails?.bulstat && (
                <p>
                  <span className="font-bold">{config.texts.bulstat[language]}</span>: {userDetails?.bulstat}
                </p>
              )}
              {userDetails?.invoiceAddress && (
                <p>
                  <span className="font-bold">{config.texts.invoiceAddress[language]}</span>:{" "}
                  {userDetails?.invoiceAddress}
                </p>
              )}
              {userDetails?.vatRegistration && (
                <p>
                  <span className="font-bold">{config.texts.vatRegistration[language]}</span>:{" "}
                  {userDetails?.vatRegistration}
                </p>
              )}
              {userDetails?.mol && (
                <p>
                  <span className="font-bold">{config.texts.mol[language]}</span>: {userDetails?.mol}
                </p>
              )}
            </div>
          </div>
        </div>
        </div>
      </div>
    </>
  );
}

export default ThankYou;
